import { ACCOUNT_ID, usePublicPortfolioByAccount } from '@utils/apiHooks';
import {
  useLargeDesktopWidth,
  useMobileWidth,
  useSlug,
  useViewAnalyzer,
} from '@utils/hooks';

import Gdpr from '@composites/Gdpr';
import { PORTFOLIOS_PATH } from '@constants/paths';
import { getEventDetails } from '@store/slices/eventSlice';
import { MODE } from '@utils/constants';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { ScrollMenuBar } from '@pages/EventHome/components/ScrollMenuBar';
import { ScrollMenuBarMobile } from '../EventHome/components/ScrollMenuBarMobile';
import CoLocatedStickyMenu from '@components/CoLocatedStickyMenu';

export const Root = styled.div`
  max-width: 100vw;
`;

const HelmetPage = () => {
  const { t } = useTranslation();
  const currentEvent = useSelector(getEventDetails)?.[0];
  const view = useViewAnalyzer();
  const portfolioSlug = useSlug(PORTFOLIOS_PATH.value) as string;
  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const isSmallerThanLargeDesktop = useLargeDesktopWidth();
  const isMobile = useMobileWidth();

  const sluggedPortfolio = useMemo(
    () => portfolios?.data?.filter(e => e?.slug === portfolioSlug)?.[0],
    [portfolioSlug, portfolios?.data]
  );

  const location = useMemo(
    () =>
      [
        `${currentEvent?.location?.name ?? ''}`,
        `${currentEvent?.location?.marker?.street ?? ''}`,
        `${currentEvent?.location?.marker?.city ?? ''}`,
        currentEvent?.location?.marker?.country
          ? t(`countries.${currentEvent?.location?.marker?.country ?? ''}`)
          : '',
      ]
        ?.filter(e => e.length > 0)
        .join(', '),
    [
      currentEvent?.location?.marker?.city,
      currentEvent?.location?.marker?.country,
      currentEvent?.location?.marker?.street,
      currentEvent?.location?.name,
      t,
    ]
  );

  const JSONMeta = useMemo(() => {
    if (view === MODE.EVENT && currentEvent)
      return (
        <script type="application/ld+json">{`
        {
          "name": "${currentEvent?.name ?? ''}",
          "startDate": "${dayjs(currentEvent?.start_date).format('DD.MM.YYYY')}",
          "endDate": "${dayjs(currentEvent?.end_date).format('DD.MM.YYYY')}",
          "description": "${currentEvent?.description ?? ''}",
          "location":"${location}",
          "url":"${window.location.href}",
          "@context": "https://schema.org/",
          "eventAttendanceMode":"${currentEvent?.type ?? ''}",
          "organizer":"we.CONECT Global Leaders GmbH",
          "keywords":"${currentEvent?.tags?.join(',') ?? ''}",
          "@type": "Event"
        }
    `}</script>
      );
    else if (view === MODE.PORTFOLIO)
      return (
        <script type="application/ld+json">{`
          {
            "name": "${sluggedPortfolio?.name ?? ''}",
            "description": "${sluggedPortfolio?.description_de ?? sluggedPortfolio?.description_en ?? ''}",
            "url":"${window.location.href}",
            "@context": "https://schema.org/",
            "organizer":"we.CONECT Global Leaders GmbH",
            "keywords": "${sluggedPortfolio?.tags_de?.join(',') ?? sluggedPortfolio?.tags_en.join(',') ?? ''}",
            "@type": "Portfolio"
          }
      `}</script>
      );
    return (
      <script type="application/ld+json">{`
        {
        }
    `}</script>
    );
  }, [
    currentEvent,
    location,
    sluggedPortfolio?.description_de,
    sluggedPortfolio?.description_en,
    sluggedPortfolio?.name,
    sluggedPortfolio?.tags_de,
    sluggedPortfolio?.tags_en,
    view,
  ]);

  return (
    <Root>
      <Helmet>{JSONMeta}</Helmet>
      {view === MODE.EVENT && !isMobile && <CoLocatedStickyMenu />}
      {view === MODE.EVENT && !isSmallerThanLargeDesktop && <ScrollMenuBar />}
      {view === MODE.EVENT && isSmallerThanLargeDesktop && (
        <ScrollMenuBarMobile />
      )}
      <Outlet />
      <Gdpr />
    </Root>
  );
};

export default HelmetPage;
